/**
 * Sub-solar geographic point (where the Sun is directly overhead) for a Date,
 * using the low-precision NOAA / Astronomical Almanac solar coordinates.
 * Accurate to ~0.01°, which is far below what the terminator shading shows.
 *
 * Returns { lat, lng } in degrees, suitable for globe.getCoords(lat, lng, alt).
 */
export function getSunSubPoint(date = new Date()) {
  const toRad = (x) => (x * Math.PI) / 180;
  const toDeg = (x) => (x * 180) / Math.PI;

  // Days since J2000.0
  const n = date.getTime() / 86_400_000 - 10_957.5;

  const L = (280.460 + 0.985_647_4 * n) % 360;        // Mean longitude
  const g = toRad((357.528 + 0.985_600_3 * n) % 360); // Mean anomaly
  const lambda = toRad(L + 1.915 * Math.sin(g) + 0.020 * Math.sin(2 * g)); // Ecliptic longitude
  const eps = toRad(23.439 - 0.000_000_4 * n);        // Obliquity of the ecliptic

  const ra = toDeg(Math.atan2(Math.cos(eps) * Math.sin(lambda), Math.cos(lambda)));
  const dec = toDeg(Math.asin(Math.sin(eps) * Math.sin(lambda)));

  const gmst = getGmstDegrees(date);

  const lng = ((((ra - gmst) % 360) + 540) % 360) - 180;
  return { lat: dec, lng };
}

/**
 * Greenwich Mean Sidereal Time in degrees [0, 360): the right ascension
 * currently overhead at longitude 0. A sky direction with right ascension α
 * sits above Earth-fixed longitude α − GMST.
 */
export function getGmstDegrees(date = new Date()) {
  const n = date.getTime() / 86_400_000 - 10_957.5; // days since J2000.0
  return (((280.460_618_37 + 360.985_647_366_29 * n) % 360) + 360) % 360;
}

/**
 * Orient the equatorial starmap sphere for a moment in time.
 *
 * The texture is a standard equirectangular sky map (RA 0h at the centre, RA
 * increasing to the left, north up). three.js's SphereGeometry viewed from
 * inside shows it mirrored, so the mesh is flipped on X; rotating it by
 * 90° − GMST then puts each star at longitude RA − GMST in the globe's
 * (lat 0, lng 0) → +Z frame — the same frame the sun and moon use.
 */
export function alignStarfield(mesh, date) {
  mesh.scale.x = -Math.abs(mesh.scale.x);
  mesh.rotation.y = ((90 - getGmstDegrees(date)) * Math.PI) / 180;
}

/**
 * The instant the globe depicts: the selected moment (ms since the epoch —
 * the end of the timeline's rolling window), or now when live (null).
 */
export function sceneTime(timeMs, now = new Date()) {
  return timeMs == null ? now : new Date(timeMs);
}
